import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { of } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { ArticleKind, IMagazine } from '../../models/article.model';
import { MagazineApiService } from '../../services/api/magazine.service';
import { IErrorResult } from '../../services/dto.service';
import { GoogleService } from '../../services/utility/google.service';
import { AppState, IMagazineState } from '../app.state';
import { EntityEffects } from '../entity.effects';
import { NewspaperSelector } from '../newspaper/newspaper.selectors';
import { RegionSelector } from '../region/region.selectors';
import { RouterSelector } from '../router/router.selectors';
import { fromMagazineActions } from './magazine.actions';
import { MagazineSelector } from './magazine.selectors';

@Injectable()
export class MagazineEffects extends EntityEffects<IMagazine, IMagazineState> {
	constructor(
		actions$: Actions,
		store: Store<AppState>,
		routerSelector: RouterSelector,
		private magazineApiService: MagazineApiService,
		private magazineSelector: MagazineSelector,
		private regionSelector: RegionSelector,
		private newspaperSelector: NewspaperSelector,
		private googleService: GoogleService
	) {
		super(actions$, store, magazineApiService, magazineSelector, routerSelector, fromMagazineActions);
	}

	public onLoad$: any = createEffect(() =>
		this.actions$.pipe(
			ofType(this.entityActions.load),
			debounceTime(10),
			concatLatestFrom(() => this.store.select(this.newspaperSelector.date)),
			concatLatestFrom(() => this.store.select(this.magazineSelector.paging)),
			concatLatestFrom(() => this.store.select(this.magazineSelector.dayRange)),
			concatLatestFrom(() => this.store.select(this.regionSelector.selected)),
			filter(([[[[, date]]]]) => date != null),
			switchMap(([[[[, date], paging], dayRange], region]) =>
				this.magazineApiService
					.search(
						{ date: moment.utc(date).toDate(), region, dateFrom: moment.utc(date).subtract(dayRange.current, 'days').toDate(), dateUntil: moment.utc(date).add(dayRange.min, 'days').toDate(), articleKind: ArticleKind.Magazine },
						paging
					)
					.pipe(
						map(data => this.entityActions.loaded({ list: data.map(rating => rating.article), ratings: data.map(rating => ({ score: rating.score, isPerfectMatch: rating.isPerfectMatch, articleId: rating.article._id })) })),
						catchError((response: IErrorResult) => of(this.entityActions.failed({ message: response.error?.error || response.error?.message })))
					)
			)
		)
	);

	public onLoadedForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(this.entityActions.loaded),
				concatLatestFrom(() => this.store.select(this.magazineSelector.paging)),
				filter(([{ list }, paging]) => list.length == paging.top),
				mergeMap(([{ list }]) => [this.googleService.viewItemList(list)])
			),
		{ dispatch: false }
	);

	public onDayRangeChanged$: any = createEffect(() =>
		this.actions$.pipe(
			ofType(fromMagazineActions.dayRangeChanged, fromMagazineActions.pagingChanged),
			concatLatestFrom(() => this.store.select(this.magazineSelector.list)),
			concatLatestFrom(() => this.store.select(this.newspaperSelector.date)),
			concatLatestFrom(() => this.store.select(this.magazineSelector.paging)),
			concatLatestFrom(() => this.store.select(this.magazineSelector.dayRange)),
			concatLatestFrom(() => this.store.select(this.regionSelector.selected)),
			filter(([[[[, date]]]]) => date != null),
			switchMap(([[[[[, list], date], paging], dayRange], region]) => {
				if (list.length >= paging.top || dayRange.current > dayRange.max) {
					return of(fromMagazineActions.extended());
				}

				return this.magazineApiService
					.search(
						{
							date: moment.utc(date).toDate(),
							region,
							dateFrom: moment.utc(date).subtract(dayRange.current, 'days').toDate(),
							dateUntil: moment.utc(date).add(dayRange.current, 'days').toDate(),
							articleKind: ArticleKind.Magazine,
						},
						paging
					)
					.pipe(
						map(data => this.entityActions.loaded({ list: data.map(rating => rating.article), ratings: data.map(rating => ({ score: rating.score, isPerfectMatch: rating.isPerfectMatch, articleId: rating.article._id })) })),
						catchError((response: IErrorResult) => of(this.entityActions.failed({ message: response.error?.error || response.error?.message })))
					);
			})
		)
	);

	public onLoadedForDayRange$ = createEffect(() =>
		this.actions$.pipe(
			ofType(this.entityActions.loaded),
			concatLatestFrom(() => this.store.select(this.magazineSelector.dayRange)),
			map(([, dayRange]) => fromMagazineActions.dayRangeChanged({ dayRange: { ...dayRange, current: dayRange.current * 2 } }))
		)
	);

	public onNavigated$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ROUTER_NAVIGATED),
			concatLatestFrom(() => this.store.select(this.routerSelector.magazineId)),
			filter(([, magazineId]) => magazineId != null),
			map(([, newspaperId]) => fromMagazineActions.loadMagazine({ id: newspaperId }))
		)
	);

	public onLoadMagazine$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromMagazineActions.loadMagazine),
			switchMap(({ id }) => this.magazineApiService.getMany([id]).pipe(map(magazines => fromMagazineActions.loadedMagazine({ magazine: magazines[0] }))))
		)
	);

	public onLoadedMagazineForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromMagazineActions.loadedMagazine),
				mergeMap(({ magazine }) => [this.googleService.viewItem(magazine)])
			),
		{ dispatch: false }
	);

	public onMagazineSelectedForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromMagazineActions.selected),
				filter(({ entity }) => entity != null),
				debounceTime(100),
				mergeMap(({ entity }) => [this.googleService.selectMagazine(entity)])
			),
		{ dispatch: false }
	);
}
